import { ref, computed } from 'vue'
import type { IAccountSidebar } from '@/shared/interfaces/account-sidebar.interface'
import type { SidebarEmits } from '@/shared/emits/sidebar.emit'
import {
  historyOptionAccountSidebar,
  internetOnDemandOptionAccountSidebar,
  logoutOptionAccountSidebar,
  profileOptionAccountSidebar
} from '@/shared/constants/sidebar-options.constant'
import { ECountry } from '@/shared/enum/country.enum'
import { ECompany } from '@/shared/enum/company.enum'
import { EPage } from '@/shared/enum/page.enum'
import { flowType as flowTypeConstants } from '../constants/flow-type.constants'
import { EProfileType } from '@/shared/enum/profile-type.enum'
import { useAppRouter } from '@/shared/composables/useAppRouter'
import { useToken } from '@/shared/composables/useToken'
import { redirectToRegenerateToken } from '@/shared/utils/regenerate-token'
import { useAppStore } from '@/stores/app'

export const useSidebar = (emit: SidebarEmits) => {
  const appStore = useAppStore()
  const { navigateToRoute } = useAppRouter()
  const { profileType, company, flowType } = useToken()

  const isOpenSidebar = ref<boolean>(false)

  const showInternetOnDemandLink = computed(() => {
    const profiles = [
      EProfileType.PREPAID,
      EProfileType.PREPAID_FTU,
      EProfileType.MODEM_PREPAID,
      EProfileType.MODEM_PREPAID_FTU
    ]
    const countries = [ECountry.GT, ECountry.HN, ECountry.NI]
    const campaigns = [ECompany.CLARO, ECompany.TELEFONICA]

    return (
      profileType.value !== null &&
      company.value !== null &&
      profiles.includes(Number(profileType.value)) &&
      countries.includes(appStore.country) &&
      campaigns.includes(Number(company.value))
    )
  })

  const showHistoryLink = computed(() => flowType.value !== flowTypeConstants.SEND_RECHARGES)

  const optionsAccountSidebar = computed((): IAccountSidebar => {
    const options = [profileOptionAccountSidebar]

    if (showInternetOnDemandLink.value) options.push(internetOnDemandOptionAccountSidebar)
    if (showHistoryLink.value) options.push(historyOptionAccountSidebar)

    options.push(logoutOptionAccountSidebar)

    return {
      id: 'my-account-menu',
      title: 'Mi cuenta',
      options
    }
  })

  const closeSidebar = () => {
    emit('change:sidebarPosition')
  }

  const handleSidebarNavigation = (namePage: EPage) => {
    closeSidebar()
    if (namePage === EPage.LANDING) {
      return redirectToRegenerateToken()
    }
    navigateToRoute(namePage)
  }

  const handleSidebarPosition = () => {
    isOpenSidebar.value = !isOpenSidebar.value
  }

  return {
    isOpenSidebar,
    optionsAccountSidebar,
    showInternetOnDemandLink,
    showHistoryLink,
    closeSidebar,
    handleSidebarPosition,
    handleSidebarNavigation
  }
}
